import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "../data/products";

const ALL = "All";

export const FeaturedProducts = () => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(ALL);

  const categories = useMemo(
    () => [ALL, ...Array.from(new Set(PRODUCTS.map((p) => p.category)))],
    []
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PRODUCTS.filter((p) => {
      const matchCat = active === ALL || p.category === active;
      const matchQuery =
        !q ||
        p.name.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q);
      return matchCat && matchQuery;
    });
  }, [query, active]);

  return (
    <section id="products" data-testid="products-section" className="bg-brand-bg py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl">
          <span className="text-sm uppercase tracking-[0.25em] text-[#8DC63F]">
            The Collection
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-brand-text">
            Products that solve real problems
          </h2>
          <p className="mt-4 text-brand-muted">
            Decades of household icons — each one engineered to make everyday life
            simpler, smarter and more comfortable.
          </p>
        </div>

        {/* search + filters */}
        <div className="mt-10 flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
          <div className="relative w-full md:max-w-sm">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted2" />
            <input
              data-testid="product-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full rounded-full border border-brand-border bg-brand-surface py-3 pl-11 pr-4 text-sm text-brand-text placeholder:text-brand-muted2 outline-none focus:border-[#8DC63F]/60 transition-colors"
            />
          </div>

          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setActive(c)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  active === c
                    ? "bg-[#8DC63F] text-[#050A15]"
                    : "border border-brand-border text-brand-muted hover:border-[#8DC63F]/50"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* grid */}
        {filtered.length === 0 ? (
          <p data-testid="no-results" className="mt-16 text-center text-brand-muted">
            No products match your search.
          </p>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p, i) => (
              <motion.article
                key={p.id}
                data-testid={`product-card-${p.id}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: (i % 3) * 0.08, duration: 0.5 }}
                className="group rounded-2xl border border-brand-border bg-brand-surface overflow-hidden hover:border-[#8DC63F]/60 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-white to-slate-100">
                  <img
                    src={p.image}
                    alt={p.name}
                    className="h-full w-full object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-4 top-4 rounded-full bg-[#050A15]/85 backdrop-blur px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-[#8DC63F]">
                    {p.badge}
                  </span>
                </div>
                <div className="p-6">
                  <div className="text-xs uppercase tracking-[0.15em] text-brand-muted2">
                    {p.category}
                  </div>
                  <div className="mt-2 flex items-start justify-between gap-3">
                    <h3 className="font-heading text-xl font-semibold text-brand-text">
                      {p.name}
                    </h3>
                    <ArrowUpRight
                      size={20}
                      className="shrink-0 text-brand-muted2 transition-colors group-hover:text-[#8DC63F]"
                    />
                  </div>
                  <p className="mt-1 text-sm font-medium text-[#8DC63F]">{p.tagline}</p>
                  <p className="mt-3 text-sm leading-relaxed text-brand-muted">
                    {p.description}
                  </p>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
